import React, { useState, useEffect } from 'react';
import { API, StatCard, DollarSign, TrendingUp, Activity, Wallet, Lock, BarChart3, FileText, AlertTriangle } from './shared';

export default function RevenueTab() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/agent-credit/escrow/revenue/analytics`)
      .then(r => r.json())
      .then(setAnalytics)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-gray-600">Loading revenue data...</div>;
  if (!analytics) return <div className="text-center py-20 text-gray-600">Unable to load revenue analytics.</div>;

  const { projections, escrow_volume, defaults } = analytics;

  const BarChart = ({ data, maxVal, color }) => (
    <div className="space-y-1.5">
      {data.map((d, i) => {
        const pct = maxVal > 0 ? (d.value / maxVal) * 100 : 0;
        return (
          <div key={i} className="flex items-center gap-3">
            <span className="text-[10px] text-gray-500 w-20 text-right font-mono shrink-0">{d.label}</span>
            <div className="flex-1 h-5 bg-white/[0.03] rounded overflow-hidden">
              <div className={`h-full ${color} rounded transition-all duration-700`} style={{ width: `${Math.max(pct, 1)}%` }} />
            </div>
            <span className="text-xs font-mono text-gray-400 w-16 text-right shrink-0">{d.display}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="revenue-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Total Protocol Revenue" value={`$${analytics.total_protocol_revenue.toFixed(2)}`} sub={`${analytics.total_credit_draws} credit draws`} color="green" />
        <StatCard icon={TrendingUp} label="Net Revenue" value={`$${analytics.net_revenue.toFixed(2)}`} sub="After evaluator costs" color="blue" />
        <StatCard icon={Activity} label="Credit Draw Volume" value={`$${(analytics.total_draw_volume || 0).toLocaleString()}`} sub={`${analytics.origination_fee_pct}% origination fee`} color="orange" />
        <StatCard icon={Wallet} label="Lender Yield Paid" value={`$${analytics.lender_yield_distributed.toFixed(2)}`} color="purple" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="rounded-2xl border border-green-500/10 bg-green-500/[0.02] p-6" data-testid="revenue-projections">
          <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" /> Revenue Projections
          </h3>
          <p className="text-xs text-gray-500 mb-5">0.5% origination fee on each credit advance</p>
          <div className="space-y-5">
            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Current Rate</div>
              <div className="grid grid-cols-3 gap-4">
                <div><div className="text-lg font-mono font-bold text-white">${(projections.current_daily_avg_volume || 0).toLocaleString()}</div><div className="text-[10px] text-gray-600">Avg Daily Volume</div></div>
                <div><div className="text-lg font-mono font-bold text-green-400">${projections.projected_monthly_revenue}</div><div className="text-[10px] text-gray-600">Monthly</div></div>
                <div><div className="text-lg font-mono font-bold text-green-400">${projections.projected_annual_revenue}</div><div className="text-[10px] text-gray-600">Annual</div></div>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Annual Revenue at Credit Volume</div>
              <BarChart
                color="bg-gradient-to-r from-green-500 to-emerald-400"
                maxVal={projections.at_500m_annual_volume}
                data={[
                  { label: '$10M/yr', value: projections.at_10m_annual_volume, display: `$${(projections.at_10m_annual_volume || 0).toLocaleString()}` },
                  { label: '$100M/yr', value: projections.at_100m_annual_volume, display: `$${(projections.at_100m_annual_volume || 0).toLocaleString()}` },
                  { label: '$500M/yr', value: projections.at_500m_annual_volume, display: `$${(projections.at_500m_annual_volume || 0).toLocaleString()}` },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-blue-500/10 bg-blue-500/[0.02] p-6" data-testid="escrow-volume-breakdown">
          <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-400" /> Escrow Volume
          </h3>
          <p className="text-xs text-gray-500 mb-5">Total: ${escrow_volume.total_volume.toLocaleString()} across {escrow_volume.total_count} escrows</p>
          <div className="space-y-3">
            {Object.entries(escrow_volume.by_status || {}).map(([status, data]) => {
              const colors = { funded: 'bg-blue-500', submitted: 'bg-yellow-500', settled: 'bg-green-500', rejected: 'bg-red-500', defaulted: 'bg-red-700' };
              const textColors = { funded: 'text-blue-400', submitted: 'text-yellow-400', settled: 'text-green-400', rejected: 'text-red-400', defaulted: 'text-red-400' };
              return (
                <div key={status} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${colors[status] || 'bg-gray-500'}`} />
                    <span className={`text-sm font-medium capitalize ${textColors[status] || 'text-gray-400'}`}>{status}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono font-bold">${data.volume.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-600">{data.count} escrows</div>
                  </div>
                </div>
              );
            })}
            {Object.keys(escrow_volume.by_status || {}).length === 0 && (
              <div className="text-center py-6 text-gray-600 text-sm">No escrow activity yet</div>
            )}
          </div>
          {defaults.count > 0 && (
            <div className="mt-5 p-4 rounded-xl bg-red-500/[0.05] border border-red-500/10">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-400" /><span className="text-xs text-red-400 uppercase font-semibold">Defaults</span></div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div><div className="text-lg font-mono font-bold text-red-400">{defaults.count}</div><div className="text-[10px] text-gray-600">Total</div></div>
                <div><div className="text-lg font-mono font-bold text-red-400">${defaults.total_debt.toLocaleString()}</div><div className="text-[10px] text-gray-600">Bad Debt</div></div>
                <div><div className="text-lg font-mono font-bold text-orange-400">{defaults.kutro_slashed.toLocaleString()}</div><div className="text-[10px] text-gray-600">KOJIRU Slashed</div></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {analytics.daily_revenue.length > 0 && (
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6" data-testid="daily-revenue-chart">
          <h3 className="font-semibold text-lg mb-5 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-green-400" /> Daily Revenue Activity
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-gray-500 uppercase border-b border-white/5">
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-right">Transactions</th>
                <th className="px-4 py-3 text-right">Fees Collected</th>
                <th className="px-4 py-3 text-right">Cumulative Revenue</th>
              </tr></thead>
              <tbody className="divide-y divide-white/[0.03]">
                {analytics.daily_revenue.map(day => (
                  <tr key={day.date} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-mono text-xs">{day.date}</td>
                    <td className="px-4 py-3 text-right font-mono">{day.transactions}</td>
                    <td className="px-4 py-3 text-right font-mono text-green-400">${day.fees.toFixed(4)}</td>
                    <td className="px-4 py-3 text-right font-mono text-blue-400">${day.cumulative.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-white/5 bg-white/[0.02] p-6" data-testid="fee-model-explainer">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-400" /> Revenue Model
        </h3>
        {analytics.revenue_by_source && Object.keys(analytics.revenue_by_source).length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Revenue by Source</div>
            <div className="space-y-2">
              {Object.entries(analytics.revenue_by_source).map(([source, data]) => {
                const colors = { credit_origination: 'text-green-400', interest_spread: 'text-blue-400', evaluator_marketplace_fee: 'text-purple-400' };
                const labels = { credit_origination: 'Credit Origination (0.5%)', interest_spread: 'Interest Spread (1.5%)', evaluator_marketplace_fee: 'Evaluator Marketplace (5%)' };
                return (
                  <div key={source} className="flex items-center justify-between py-1.5">
                    <span className={`text-xs ${colors[source] || 'text-gray-400'}`}>{labels[source] || source}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-mono text-gray-500">{data.count} txns</span>
                      <span className={`text-sm font-mono font-bold ${colors[source] || 'text-gray-400'}`}>${data.total.toFixed(4)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { title: 'Credit Origination', pct: '0.5%', desc: 'Charged on each credit advance at draw time. Scales with deal size.', color: 'green' },
            { title: 'Interest Spread', pct: '1.5%', desc: "Kojiru's cut between borrower APR and lender yield. Accrues daily on outstanding credit.", color: 'blue' },
            { title: 'Evaluator Marketplace', pct: '5%', desc: 'Platform cut of all evaluator fees. Incentivizes quality marketplace operation.', color: 'purple' },
            { title: 'Default Penalty', pct: 'Slash', desc: 'Staked USDC collateral liquidated 1:1 on default and routed atomically to the harmed party.', color: 'orange' },
          ].map(item => (
            <div key={item.title} className={`p-4 rounded-xl bg-${item.color}-500/[0.05] border border-${item.color}-500/10`}>
              <div className={`text-xs text-${item.color}-400 uppercase font-semibold mb-2`}>{item.title}</div>
              <div className="text-2xl font-mono font-bold text-white">{item.pct}</div>
              <p className="text-xs text-gray-500 mt-2">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
